import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Receipt } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '~/components/data';
import { HeroMetric } from '~/components/domain';
import { DateRangePicker, PeriodPresetChips } from '~/components/primitives';
import { fetchTaxSummary } from '~/lib/api/finance';
import shared from './_auth._shared.module.css';
import styles from './_auth.finance.tax-summary.module.css';

/**
 * S13-FE.7 — Tax summary page.
 */

export const Route = createFileRoute('/_auth/finance/tax-summary')({ component: TaxSummaryPage });

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
// S-FIX-6: default YTD (см. cash-flow).
function firstOfYear(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-01-01`;
}

function TaxSummaryPage(): JSX.Element {
  const [dateFrom, setDateFrom] = useState(firstOfYear());
  const [dateTo, setDateTo] = useState(today());
  const query = useQuery({
    queryKey: ['finance', 'tax-summary', dateFrom, dateTo],
    queryFn: () => fetchTaxSummary({ date_from: dateFrom, date_to: dateTo }),
    enabled: Boolean(dateFrom && dateTo),
  });
  const p = query.data;

  return (
    <div>
      <PageHeader title={m.nav_finance_tax_summary()} />

      <div className={styles.filterRow}>
        <DateRangePicker
          label={m.cogs_filter_date_from()}
          value={{ from: dateFrom || null, to: dateTo || null }}
          onChange={(r) => {
            setDateFrom(r.from ?? '');
            setDateTo(r.to ?? '');
          }}
        />
        {/* FE-279 Pass 24/27: shared preset chips refactored to PeriodPresetChips. */}
        <PeriodPresetChips
          onChange={(r) => {
            setDateFrom(r.from);
            setDateTo(r.to);
          }}
        />
      </div>

      {!p && !query.isLoading ? (
        <EmptyState title={m.nav_finance_tax_summary()} icon={<Receipt size={32} />} />
      ) : p ? (
        // FE-262 Pass 4: use HeroMetric pattern per STAITER_UI.md §4.3
        // (раньше — plain table rows без card-обёртки, без --text-hero).
        // FE-176/268 Pass 52: ProvenanceChip — все tax aggregates computed
        // в STAITER (cost-engine + invoice tables) — source='staiter'.
        <div className={shared.heroRow}>
          {(() => {
            const provenance = {
              source: 'staiter' as const,
              fetched_at: new Date().toISOString(),
            };
            return (
              <>
                <HeroMetric
                  label={m.tax_summary_vat_payable()}
                  value={p.vat_payable}
                  compact
                  provenance={provenance}
                />
                <HeroMetric
                  label={m.tax_summary_vat_credit()}
                  value={p.vat_credit}
                  compact
                  provenance={provenance}
                />
                <HeroMetric
                  label={m.tax_summary_vat_net()}
                  value={p.vat_net}
                  compact
                  provenance={provenance}
                />
                <HeroMetric
                  label={m.tax_summary_income_tax_advance()}
                  value={p.income_tax_advance}
                  compact
                  provenance={provenance}
                />
              </>
            );
          })()}
        </div>
      ) : null}
    </div>
  );
}
